var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState } from 'react';
import { isEmpty } from 'lodash-es';
import Markdown from 'markdown-to-jsx';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { renderLink } from '../../clearstep/util/MarkdownHelpers';
import { MessageType } from '../../clearstep/proto/logging_pb';
import './TriageRecommendationeColorMapping';
import { useRingStyle } from '../../tailwind_common/util';
import { DropdownIcon } from '../../svg/DropdownIcon';
import { trackEvent } from '../../clearstep/util/Split';
import { Reload } from '../../SVG';
import Button from '../../tailwind_common/Button';
import { STATUS, useWhyThisResult } from './WhyThisResultHook';
var alertDiv = function (_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (React.createElement("div", __assign({}, props, { role: "alert" }), children));
};
var renderSummaryText = function (primaryCareSummary, options) {
    if (options === void 0) { options = {}; }
    var _a = options.textColor, textColor = _a === void 0 ? '' : _a;
    return (React.createElement(Markdown, { options: {
            forceBlock: true,
            overrides: {
                p: function (props) {
                    props.className = 'font-semibold leading-6 text-lg lg:text-2xl lg:leading-8';
                    return alertDiv(props);
                },
                a: function (_a) {
                    var href = _a.href, children = _a.children;
                    return renderLink(href, children, MessageType.CLICKED_PARTNER_CARE_OPTION, twMerge(textColor, 'focus:underline'), {
                        extraData: { linkType: 'triage-summary' },
                    });
                },
            },
        } }, primaryCareSummary));
};
export var TriageRecommendation = function (props) {
    var theme = props.theme, primaryCareSummary = props.primaryCareSummary, conversationId = props.conversationId, bestCareType = props.bestCareType;
    var _a = useWhyThisResult({
        initialContent: props.whyThisResultContent,
        conversationId: conversationId,
        bestCareType: bestCareType,
    }), whyThisResultState = _a[0], refreshResults = _a[1];
    var _b = useState(false), dropdownIsExpanded = _b[0], setDropdownIsExpanded = _b[1];
    var t = useTranslation().t;
    var ringStyle = useRingStyle();
    var _c = theme || {}, backgroundColor = _c.backgroundColor, textColor = _c.textColor;
    return (React.createElement("div", { className: twMerge('flex w-full flex-col rounded-md', backgroundColor, textColor) },
        React.createElement("div", { className: "flex items-center p-5 text-lg font-extrabold leading-none lg:text-2xl mobile:p-4", "data-cy": "triageRecommendation" }, renderSummaryText(primaryCareSummary, { textColor: textColor })),
        !isEmpty(conversationId) && whyThisResultState.isActive && (React.createElement("div", { className: "border-x-0 border-b-0 border-t border-solid border-gray-400" },
            React.createElement("button", { className: "tw-focus-keyboard-only flex w-full items-center justify-between p-5 text-base text-gray-700 mobile:p-4", style: ringStyle, onClick: function () {
                    var expanding = !dropdownIsExpanded;
                    if (expanding) {
                        trackEvent('user', 'expand_triage_info_section', {});
                    }
                    setDropdownIsExpanded(!dropdownIsExpanded);
                } },
                t('whyThisResult.title', 'Why this result?'),
                React.createElement(DropdownIcon, { className: twMerge('flex transform transition-all duration-360 ease-in-out', dropdownIsExpanded && 'rotate-180') })),
            dropdownIsExpanded && (React.createElement("div", { className: "px-5 pb-5 text-base text-gray-500 mobile:p-4" },
                whyThisResultState.status === STATUS.LOADING && (React.createElement("div", { className: "grid animate-pulse grid-cols-3 gap-4" },
                    React.createElement("div", { className: "col-span-3 mb-2 h-3 rounded bg-gray-200" }),
                    React.createElement("div", { className: "col-span-2 mb-2 h-3 rounded bg-gray-200 mobile:col-span-3" }),
                    React.createElement("div", { className: "hidden h-3 rounded bg-gray-200 mobile:col-span-2 mobile:grid" }))),
                whyThisResultState.status === STATUS.LOADED && (React.createElement(Markdown, { options: {
                        overrides: {
                            ul: function (props) { return (React.createElement("ul", __assign({}, props, { className: twMerge(props.className, 'leadin-6 my-2 ml-4 ') }))); },
                            p: function (props) { return (React.createElement("p", __assign({}, props, { className: twMerge(props.className, 'font-normal leading-6') }))); },
                        },
                    } }, whyThisResultState.content || '')),
                whyThisResultState.status === STATUS.ERROR && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "mb-4" }, t('whyThisResult.error', "We're having trouble loading the explanation at the moment. We apologize for the inconvenience.")),
                    React.createElement(Button, { secondary: true, className: "border-gray-300 bg-white hover:bg-white", innerClassName: "flex-row text-gray-700 items-center", onClick: refreshResults },
                        React.createElement(Reload, { className: "mr-2" }),
                        t('reload', 'Reload'))))))))));
};
export default TriageRecommendation;
